import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ApiKey, ApiUrl, SessionKey, LanguageKey, WebUrl } from "../../util/Constant";
import ApiEngine from "../../util/ApiEngine";
import { createFormBody, stringIsNullOrEmpty } from "../../util/Util";
import { useDispatch, useSelector } from "react-redux";
import { setBusy, setIdle } from "../../redux/AppAction";
import QRCode from "qrcode.react";
import { InlineShareButtons } from "sharethis-reactjs";
import parse from 'react-html-parser';
import { useHistory } from 'react-router-dom';


const TARGET_INVITES = 3;
const WINNER_DEPOSIT_TARGET = 1000;
const SLOTS_PER_GROUP = 4;
const GROUPS_PER_ROW = 4;

/** Sort order for group keys from API (e.g. "A" … "L"). */
function groupLabelRank(label) {
  if (label == null || label === "" || label === "—") {
    return 9999;
  }
  const s = String(label).trim();
  if (/^[a-z]$/i.test(s)) {
    const code = s.toUpperCase().charCodeAt(0);
    if (code >= 65 && code <= 90) {
      return code - 64;
    }
  }
  const m = s.match(/^group\s*([a-z])/i);
  if (m) {
    const code = m[1].toUpperCase().charCodeAt(0);
    if (code >= 65 && code <= 90) {
      return code - 64;
    }
  }
  const n = parseInt(s, 10);
  if (!Number.isNaN(n)) {
    return n;
  }
  return 1000;
}

/** Header text: single-letter keys → localized "Group A"; otherwise uppercase as-is. */
function formatGroupHeaderTitle(label, t) {
  if (label == null || label === "") {
    return "";
  }
  if (label === "—") {
    return "—";
  }
  const s = String(label).trim();
  if (/^[a-z]$/i.test(s)) {
    return t("WORLD_CUP_PARLAY_GROUP_LETTER", { letter: s.toUpperCase() });
  }
  return s.toUpperCase();
}

/** Grouping key from each row — matches API `groupLabel` (e.g. "A", "B"). */
function teamGroupKey(team) {
  if (!team) {
    return "";
  }
  const raw = team.groupLabel ?? team.GroupLabel;
  if (raw == null) {
    return "";
  }
  return String(raw).trim();
}

const WorldCupParlay = () => {
  const { t } = useTranslation();
  const { isLoggedIn } = useSelector((state) => state.authState);
  var _dispatch = useDispatch();
  const [isOpen, setIsOpen] = useState(false);
  const [isShareOpen, setIsShareOpen] = useState(false);
  const [step, setStep] = useState(1);
  const [selectedTeams, setSelectedTeams] = useState([]);
  const [championTeamId, setChampionTeamId] = useState(null);
  const [isLocked, setIsLocked] = useState(false);
  const [isLockConfirmOpen, setIsLockConfirmOpen] = useState(false);
  const [lockConfirmStep, setLockConfirmStep] = useState(1);
  const [isMaxTeamsNoticeOpen, setIsMaxTeamsNoticeOpen] = useState(false);
  const [inviteCount, setInviteCount] = useState(0);
  const [inviteRewardAmount, setInviteRewardAmount] = useState(0);
  const [inviteRegisterCount, setInviteRegisterCount] = useState(0);
  const [inviteDepositCount, setInviteDepositCount] = useState(0);
  const [totalDeposit, setTotalDeposit] = useState(0);
  const [isClaim, setIsClaim] = useState(false);
  const [referralCode, setReferralCode] = useState("");
  const [referralCodeList, setReferralCodeList] = useState([]);
  const [selectedReferralCodeId, setSelectedReferralCodeId] = useState("");
  const [sharePanel, setSharePanel] = useState("pick");
  const [howtoExpanded, setHowtoExpanded] = useState(false);
  const [inviteSectionExpanded, setInviteSectionExpanded] = useState(false);
  const [registerUrl, setRegisterUrl] = useState("");
  const [copySuccessVisible, setCopySuccessVisible] = useState(false);
  const copyToastTimerRef = useRef(null);
  const _APPEND_REGISTER_URL = "/register?code=";
  const [tnc, setTnc] = useState("");
  var _history = useHistory();

  let activeTheme = useSelector(state => state.authState.userData.selectedTheme);
  const inviteRegisterPercent = Math.min((inviteRegisterCount / inviteCount) * 100, 100);
  const inviteDepositPercent = Math.min((inviteDepositCount / inviteCount) * 100, 100);
  const inviteTotalDepositPercent = Math.min((totalDeposit / WINNER_DEPOSIT_TARGET) * 100, 100);
  const [teamList, setTeamList] = useState([]);
  const [winnerResult, setWinnerResult] = useState({
    isWinner: false,
    totalDesposit: 0,
    isClaim: false,
    claimAvailable: false,
    winAmount: 0,
  });
  const [isWinnerClaimPopupOpen, setIsWinnerClaimPopupOpen] = useState(false);
  const [isWinnerClaimSuccessOpen, setIsWinnerClaimSuccessOpen] = useState(false);
  const championTeam =
    championTeamId != null ? teamList.find((item) => item.id === championTeamId) : null;
  const winnerDepositPercent = Math.min(
    (Number(winnerResult.totalDesposit) / WINNER_DEPOSIT_TARGET) * 100,
    100
  );
  const [submitErrorOpen, setSubmitErrorOpen] = useState(false);
  const [submitErrorMessage, setSubmitErrorMessage] = useState("");

  const showSubmitErrorPopup = (responseJson) => {
    const raw = responseJson && responseJson[ApiKey._API_MESSAGE_KEY];
    const text = stringIsNullOrEmpty(raw) ? t("ERROR_PLEASE_TRY_AGAIN") : t(String(raw));
    setSubmitErrorMessage(text);
    setSubmitErrorOpen(true);
  };
  const groupedTeams = useMemo(() => {
    const map = new Map();
    teamList.forEach((team) => {
      const key = teamGroupKey(team);
      const bucket = key === "" ? "—" : key;
      if (!map.has(bucket)) {
        map.set(bucket, []);
      }
      map.get(bucket).push(team);
    });
    return Array.from(map.entries())
      .map(([label, teams]) => ({
        label,
        teams: [...teams].sort((a, b) =>
          String(a.countryName || "").localeCompare(String(b.countryName || ""), undefined, {
            sensitivity: "base",
          })
        ),
      }))
      .sort((a, b) => {
        const d = groupLabelRank(a.label) - groupLabelRank(b.label);
        return d !== 0 ? d : String(a.label).localeCompare(String(b.label), undefined, { sensitivity: "base" });
      });
  }, [teamList]);

  const groupRows = useMemo(() => {
    const rows = [];
    for (let i = 0; i < groupedTeams.length; i += GROUPS_PER_ROW) {
      rows.push(groupedTeams.slice(i, i + GROUPS_PER_ROW));
    }
    return rows;
  }, [groupedTeams]);

  const handleOpen = () => {
    getWorldCupEventDetail();
    fetchWinnerResultDetail();
  };

  const handleClose = () => {
    setIsOpen(false);
    setHowtoExpanded(false);
    setInviteSectionExpanded(false);
    setIsShareOpen(false);
    setSharePanel("pick");
    setIsWinnerClaimPopupOpen(false);
    setIsWinnerClaimSuccessOpen(false);
    setSubmitErrorOpen(false);
    setSubmitErrorMessage("");
  };

  const fetchWinnerResultDetail = async () => {
    const responseJson = await ApiEngine.get(ApiUrl._API_GET_WINNER_RESULT_DETAIL);
    if (responseJson[ApiKey._API_SUCCESS_KEY]) {
      const d = responseJson[ApiKey._API_DATA_KEY] || {};
      setWinnerResult({
        isWinner: Boolean(d.isWinner),
        totalDesposit: Number(d.totalDesposit) || 0,
        isClaim: Boolean(d.isClaim),
        claimAvailable: Boolean(d.claimAvailable),
        winAmount: Number(d.winAmount) || 0,
      });
    }
  };

  const copyReferralCode = async () => {
    if (!referralCode) {
      return;
    }
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(referralCode);
        setCopySuccessVisible(true);
        if (copyToastTimerRef.current != null) {
          window.clearTimeout(copyToastTimerRef.current);
        }
        copyToastTimerRef.current = window.setTimeout(() => {
          setCopySuccessVisible(false);
          copyToastTimerRef.current = null;
        }, 2200);
      }
    } catch {
      /* ignore */
    }
  };

  const copyRegisterUrl = async () => {
    if (!registerUrl) {
      return;
    }
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(registerUrl);
        setCopySuccessVisible(true);
        if (copyToastTimerRef.current != null) {
          window.clearTimeout(copyToastTimerRef.current);
        }
        copyToastTimerRef.current = window.setTimeout(() => {
          setCopySuccessVisible(false);
          copyToastTimerRef.current = null;
        }, 2200);
      }
    } catch {
      /* ignore */
    }
  };

  useEffect(() => {
    return () => {
      if (copyToastTimerRef.current != null) {
        window.clearTimeout(copyToastTimerRef.current);
      }
    };
  }, []);

  const onSelectTeam = (teamId) => {
    if (isLocked || step !== 1) {
      return;
    }

    const alreadySelected = selectedTeams.includes(teamId);
    if (alreadySelected) {
      setSelectedTeams(selectedTeams.filter((id) => id !== teamId));
      return;
    }

    if (selectedTeams.length >= 4) {
      setIsMaxTeamsNoticeOpen(true);
      return;
    }

    setSelectedTeams([...selectedTeams, teamId]);
  };

  const onSubmitSelection = () => {
    if (selectedTeams.length !== 4) {
      return;
    }
    setStep(2);
  };

  const onLockPicks = () => {
    if (isLocked || !championTeamId) {
      return;
    }
    setLockConfirmStep(1);
    setIsLockConfirmOpen(true);
  };

  const closeLockConfirm = () => {
    setIsLockConfirmOpen(false);
    setLockConfirmStep(1);
  };


  const getWorldCupEventDetail = async () => {
    _dispatch(setBusy());
    var responseJson = await ApiEngine.get(
      ApiUrl._API_GET_WORLD_CUP_EVENT_DETAIL
    );

    if (responseJson[ApiKey._API_SUCCESS_KEY]) {
      let data = responseJson[ApiKey._API_DATA_KEY];
      setTeamList(data["worldCupTeams"]);
      setInviteCount(data["inviteCount"]);
      setInviteRewardAmount(data["inviteReward"]);
      setTnc(data["tnc"]);
      setTotalDeposit(data["totalDeposit"]);
      const code = data["referralCode"];
      if (!stringIsNullOrEmpty(code)) {
        setReferralCode(code);
        setRegisterUrl(window.location.origin + _APPEND_REGISTER_URL + code);
      }
      const referralList = Array.isArray(data["referralCodeList"]) ? data["referralCodeList"] : [];
        setReferralCodeList(referralList);
        if (referralList.length > 0) {
          const matched = referralList.find((item) => item.code === code);
          setSelectedReferralCodeId(String(matched ? matched.id : referralList[0].id));
        } else {
          setSelectedReferralCodeId("");
        }
      if (data["isLocked"]) {
        setStep(2);
        setSelectedTeams(data["pickTeams"]);
        setChampionTeamId(data["championTeamId"]);
        setIsClaim(data["isClaim"]);
        setIsLocked(data["isLocked"]);
        setInviteRegisterCount(data["countInviteFriendRegister"] ?? 0);
        setInviteDepositCount(data["countInviteFriendDeposit"] ?? 0);
        await fetchWinnerResultDetail();
      } else {
        setStep(1);
        setWinnerResult({
          isWinner: false,
          totalDesposit: 0,
          isClaim: false,
          claimAvailable: false,
          winAmount: 0,
        });
      }
      setIsOpen(true);
      _dispatch(setIdle());
    }
  };

  const SubmitLocked = async () => {
    _dispatch(setBusy());
    let params = {
      championTeamId: championTeamId,
      pickTeamIds: selectedTeams.join(","),
      referralCodeId: selectedReferralCodeId
    };
    let responseJson = await ApiEngine.post(
      ApiUrl._API_LOCKED_WORLD_CUP_MEMBER_PICK,
      createFormBody(params));

    if (responseJson[ApiKey._API_SUCCESS_KEY]) {
      //let data = responseJson[ApiKey._API_DATA_KEY];
      //setLoginFailCount(data);

      setIsLocked(true);
      setIsLockConfirmOpen(false);
      setLockConfirmStep(1);
      setSharePanel("pick");
      setIsShareOpen(true);
      getWorldCupEventDetail();

    } else {
      showSubmitErrorPopup(responseJson);
    }
    _dispatch(setIdle());
    
  }

  const SubmitRewardClaim = async () => {
    _dispatch(setBusy());
    let responseJson = await ApiEngine.post(
      ApiUrl._API_CREATED_WORLD_CUP_REWARD);

    if (responseJson[ApiKey._API_SUCCESS_KEY]) {
      getWorldCupEventDetail();
    } else {
      showSubmitErrorPopup(responseJson);
    }
    _dispatch(setIdle());
  };

  const SubmitWinnerPrizeClaim = async () => {
    _dispatch(setBusy());
    const responseJson = await ApiEngine.post(
      ApiUrl._API_CLAIM_WORLD_CUP_WINNER_PRIZE,
      createFormBody({})
    );
    if (responseJson[ApiKey._API_SUCCESS_KEY]) {
      fetchWinnerResultDetail();
      setIsWinnerClaimPopupOpen(false);
      setIsWinnerClaimSuccessOpen(true);
    } else {
      showSubmitErrorPopup(responseJson);
    }
    _dispatch(setIdle());
  };

  const renderStep1GroupGrid = () => (
    <div className="world-cup-step1-groups">
      {groupRows.map((row, rowIdx) => (
        <div key={`group-row-${rowIdx}`} className="world-cup-group-row">
          {row.map((group) => (
            <div key={group.label} className="world-cup-group-col">
              <div className="world-cup-group-header">{formatGroupHeaderTitle(group.label, t)}</div>
              <div className="world-cup-group-slots">
                {Array.from({ length: SLOTS_PER_GROUP }).map((_, slotIdx) => {
                  const team = group.teams[slotIdx];
                  if (!team) {
                    return (
                      <div
                        key={`${group.label}-tbd-${slotIdx}`}
                        className="world-cup-group-slot world-cup-group-slot--tbd"
                      >
                        <span className="world-cup-group-slot-flag world-cup-group-slot-flag--tbd" />
                      </div>
                    );
                  }
                  const active = selectedTeams.includes(team.id);
                  return (
                    <>
                      <button
                        key={team.id}
                        type="button"
                        className={`world-cup-group-slot world-cup-group-slot--team${active ? " is-selected" : ""}`}
                        onClick={() => onSelectTeam(team.id)}
                      >
                        <img
                          src={`/img/world-cup/${team.code}.svg`}
                          alt=""
                          className="world-cup-group-slot-flag"
                        />
                      </button>
                      <div style={{ fontSize: '10px' }}>{team.shortName}</div>
                    </>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      ))}
    </div>
  );

  const ParlaySectionTitle = ({ children }) => (
    <div className="world-cup-parlay-section-heading">
      <span className="world-cup-parlay-section-line" aria-hidden="true" />
      <span className="world-cup-parlay-section-diamond" aria-hidden="true" />
      <span className="world-cup-parlay-section-sub-label">{children}</span>
      <span className="world-cup-parlay-section-diamond" aria-hidden="true" />
      <span className="world-cup-parlay-section-line" aria-hidden="true" />
    </div>
  );

  const ParlaySectionSubTitle = ({ children }) => (
    <div className="world-cup-parlay-section-heading">
      <span className="world-cup-parlay-section-line" aria-hidden="true" />
      <span className="world-cup-parlay-section-diamond" aria-hidden="true" />
      <span className="world-cup-parlay-section-label">{children}</span>
      <span className="world-cup-parlay-section-diamond" aria-hidden="true" />
      <span className="world-cup-parlay-section-line" aria-hidden="true" />
    </div>
  );

  //const onClickShare = async () => {
  //  window.open(registerUrl, '_blank', 'noopener,noreferrer')
  //};


  const ShareButton = useMemo(() => {
    if (!stringIsNullOrEmpty(registerUrl)) {
      return (
        <InlineShareButtons
          config={{
            alignment: 'center',
            color: 'social',
            enabled: true,
            language: 'en',
            networks: ['whatsapp', 'facebook', 'twitter', 'wechat', 'telegram'],
            padding: 12,
            radius: 35,
            size: 40,

            url: registerUrl,
          }}
        />
      )
    }
    else {
      return null;
    }

  }, [registerUrl]);

  return (
    <>
      {isLoggedIn &&
        <div className="world-cup-trigger" onClick={handleOpen}>
          <img
            src={"/img/world-cup/App_"
              + (localStorage.getItem(SessionKey._LANGUAGE) && localStorage.getItem(SessionKey._LANGUAGE) !== 'undefined'
                ? localStorage.getItem(SessionKey._LANGUAGE) === 'en' || localStorage.getItem(SessionKey._LANGUAGE) === 'zh' || localStorage.getItem(SessionKey._LANGUAGE) === 'ms'
                  ? localStorage.getItem(SessionKey._LANGUAGE)
                  : 'en'
                : 'en')
              + ".png"}
            alt={t("WORLD_CUP_PARLAY_ALT_TRIGGER")}
          />
        </div>
      }
      {isOpen && (
        <div className="world-cup-modal-overlay">
          <div className="world-cup-header">
            {step === 2 &&
              !isLocked && (
                <button
                  type="button"
                  className="world-cup-back-btn"
                  onClick={() => {
                    setChampionTeamId(null);
                    setStep(1);
                  }}
                  aria-label={t("WORLD_CUP_PARLAY_BACK")}
                >
                  ←
                </button>
              )
            }

            <img
              src={"/img/world-cup/Header_"
                + (localStorage.getItem(SessionKey._LANGUAGE) && localStorage.getItem(SessionKey._LANGUAGE) !== 'undefined'
                  ? localStorage.getItem(SessionKey._LANGUAGE) === 'en' || localStorage.getItem(SessionKey._LANGUAGE) === 'zh' || localStorage.getItem(SessionKey._LANGUAGE) === 'ms'
                    ? localStorage.getItem(SessionKey._LANGUAGE)
                    : 'en'
                  : 'en')
                + ".png"}
              alt={t("WORLD_CUP_PARLAY_ALT_HEADER")} />

            <button type="button" className="world-cup-close" onClick={handleClose}>
              ×
            </button>
          </div>
          <div
            className="world-cup-modal-card"
          >
            {/*<div className="world-cup-modal-header">*/}
            {/*  {step === 2 ? (*/}
            {/*    <div className="world-cup-step2-header-left">*/}
            {/*      {!isLocked ? (*/}
            {/*        <button*/}
            {/*          type="button"*/}
            {/*          className="world-cup-back-btn"*/}
            {/*          onClick={() => {*/}
            {/*            setChampionTeamId(null);*/}
            {/*            setStep(1);*/}
            {/*          }}*/}
            {/*          aria-label="Back"*/}
            {/*        >*/}
            {/*          ←*/}
            {/*        </button>*/}
            {/*      ) : null}*/}
            {/*      <span className="world-cup-parlay-brand">My Parlay</span>*/}
            {/*    </div>*/}
            {/*  ) : (*/}
            {/*    <span className="world-cup-header-corner" aria-hidden="true" />*/}
            {/*  )}*/}
            {/*</div>*/}

            {step === 1 && (
              <>
                <div><img src={"/img/world-cup/FRAME_"
                  + (localStorage.getItem(SessionKey._LANGUAGE) && localStorage.getItem(SessionKey._LANGUAGE) !== 'undefined'
                    ? localStorage.getItem(SessionKey._LANGUAGE) === 'en' || localStorage.getItem(SessionKey._LANGUAGE) === 'zh' || localStorage.getItem(SessionKey._LANGUAGE) === 'ms'
                      ? localStorage.getItem(SessionKey._LANGUAGE)
                      : 'en'
                    : 'en')
                  + ".png"} alt={t("WORLD_CUP_PARLAY_STEP_HEADER_ALT")} style={{ width: '100%', height: '75px' }} /></div>
                <div className="world-cup-subtitle">{t("WORLD_CUP_PARLAY_SUBTITLE")}</div>
                {renderStep1GroupGrid()}

                <div className="world-cup-actions">
                  <button
                    className="world-cup-btn world-cup-btn-primary"
                    disabled={selectedTeams.length !== 4}
                    onClick={onSubmitSelection}
                  >
                    {t("WORLD_CUP_PARLAY_SUBMIT_COUNT", { current: selectedTeams.length })}
                  </button>
                </div>
                <div className="world-cup-howto">
                  <button
                    type="button"
                    className="world-cup-collapsible-header"
                    onClick={() => setHowtoExpanded((v) => !v)}
                    aria-expanded={howtoExpanded}
                  >
                    <span className="world-cup-collapsible-title world-cup-collapsible-title--howto">
                      {t("WORLD_CUP_PARLAY_HOWTO_TITLE")}
                    </span>
                    <span className="world-cup-collapsible-chevron" aria-hidden="true">
                      {howtoExpanded ? "▲" : "▼"}
                    </span>
                  </button>
                  {howtoExpanded ? (
                    <div className="world-cup-collapsible-panel">
                      <div className="world-cup-howto-content">
                        <div className="world-cup-howto-icon" aria-hidden="true">
                          <img src="/img/world-cup/icon.png" alt={t("WORLD_CUP_PARLAY_ALT_TROPHY")} />
                        </div>
                        <ul className="world-cup-howto-list">
                          {parse(tnc)}
                        </ul>
                      </div>
                    </div>
                  ) : null}
                </div>
              </>
            )}

            {step === 2 && (
              <div className="world-cup-step2">
                <ParlaySectionTitle>{t("WORLD_CUP_PARLAY_MY_SEMIFINALISTS")}</ParlaySectionTitle>
                <div className="world-cup-parlay-semi-grid">
                  {selectedTeams.map((teamId) => {
                    const selectedTeam = teamList.find((item) => item.id === teamId);
                    const isChampionPick = championTeamId === teamId;
                    const tileClass = `world-cup-parlay-team-tile${isChampionPick ? " is-champion-pick" : ""}${!isLocked ? " world-cup-parlay-team-tile--selectable" : ""}`;
                    const inner = selectedTeam ? (
                      <>
                        <img
                          src={`/img/world-cup/${selectedTeam.code}.svg`}
                          alt=""
                          className="world-cup-parlay-tile-flag"
                        />
                        <span className="world-cup-parlay-tile-name">{selectedTeam.countryName}</span>
                      </>
                    ) : (
                      teamId
                    );
                    if (isLocked) {
                      return (
                        <div key={teamId} className={tileClass}>
                          {inner}
                        </div>
                      );
                    }
                    return (
                      <button
                        key={teamId}
                        type="button"
                        className={tileClass}
                        onClick={() => setChampionTeamId(teamId)}
                      >
                        {inner}
                      </button>
                    );
                  })}
                </div>

                <ParlaySectionSubTitle>{t("WORLD_CUP_PARLAY_PREDICT_CHAMPION")}</ParlaySectionSubTitle>
                <div className="world-cup-parlay-champion-display">
                  {championTeam ? (
                    <>
                      <img
                        src={`/img/world-cup/${championTeam.code}.svg`}
                        alt=""
                        className="world-cup-parlay-champion-display-flag"
                      />
                      <span className="world-cup-parlay-champion-display-name">{championTeam.countryName}</span>
                    </>
                  ) : (
                    <div className="world-cup-parlay-champion-placeholder">
                      {t("WORLD_CUP_PARLAY_SELECT_CHAMPION")}
                    </div>
                  )}
                </div>

                <div className="world-cup-parlay-lock-hero">
                  <div className="world-cup-parlay-lock-burst" aria-hidden="true" />
                  <div className="world-cup-parlay-lock-stars" aria-hidden="true" />
                  <div className="world-cup-modal-card-lottie-wrap" aria-hidden="true">
                    {winnerResult.isWinner &&
                      <iframe className="congratulations-iframe" src="https://lottie.host/embed/f53703f6-2f21-4c12-aba1-849edc808aca/qTbuVDWkTJ.json" />
                    }
                  </div>
                  <img
                    className={`world-cup-parlay-trophy${isLocked ? " is-locked" : ""}`}
                    src="/img/world-cup/FIFA_Trophy.png"
                    alt=""
                  />
                  {isLocked ? (
                    <div className={winnerResult.isWinner ? "world-cup-parlay-lock-ribbon is-winner" : "world-cup-parlay-lock-ribbon is-locked"} role="status">
                      <img
                        src={winnerResult.isWinner ? "/img/world-cup/champoin.png" : ""}
                        alt={winnerResult.isWinner ? t("WORLD_CUP_PARLAY_ALT_CONGRATULATIONS") : t("")}
                      />
                      <img
                        src={winnerResult.isWinner ? "/img/world-cup/Congratulations.png" : "/img/world-cup/Locked_btn.png"}
                        alt={winnerResult.isWinner ? t("WORLD_CUP_PARLAY_ALT_CONGRATULATIONS") : t("WORLD_CUP_PARLAY_ALT_LOCKED_BTN")}
                      />
                    </div>
                  ) : (
                    <div
                      className={`world-cup-parlay-lock-ribbon${championTeamId == null ? " is-disabled" : " is-popout"}`}
                      onClick={onLockPicks}
                      disabled={selectedTeams.length !== 4 || championTeamId == null}
                    >
                      <img src="/img/world-cup/Lock_btn.png" alt={t("WORLD_CUP_PARLAY_ALT_LOCK_BTN")} />
                    </div>
                  )}
                </div>

                <div className="world-cup-step2-actions">
                  {isLocked && winnerResult.isWinner ? (
                    winnerResult.isClaim ? (
                      <button
                        type="button"
                        className="world-cup-btn world-cup-btn-primary world-cup-step2-invite-btn"
                        disabled
                      >
                        {t("CLAIMED")}
                      </button>
                    ) : (
                      <button
                        type="button"
                        className="world-cup-btn world-cup-btn-primary world-cup-step2-invite-btn"
                        onClick={() => setIsWinnerClaimPopupOpen(true)}
                      >
                        {t("WORLD_CUP_PARLAY_CLAIM_PRIZE")}
                      </button>
                    )
                  ) : (
                    <button
                      type="button"
                      className="world-cup-btn world-cup-btn-primary world-cup-step2-invite-btn"
                      disabled={!isLocked}
                      onClick={() => {
                        setSharePanel("pick");
                        setIsShareOpen(true);
                      }}
                    >
                      {t("WORLD_CUP_PARLAY_INVITE_FRIENDS")}
                    </button>
                  )}
                </div>

                <div className="world-cup-invite-card world-cup-invite-card--step2">
                  <button
                    type="button"
                    className="world-cup-collapsible-header"
                    onClick={() => setInviteSectionExpanded((v) => !v)}
                    aria-expanded={inviteSectionExpanded}
                  >
                    <span className="world-cup-collapsible-title world-cup-collapsible-title--invite">
                      {t("WORLD_CUP_PARLAY_INVITE_FRIENDS")}
                    </span>
                    <span className="world-cup-collapsible-chevron" aria-hidden="true">
                      {inviteSectionExpanded ? "▲" : "▼"}
                    </span>
                  </button>
                  {inviteSectionExpanded ? (
                    <div className="world-cup-collapsible-panel">
                      <div className="world-cup-progress-rows">
                        <div className="world-cup-progress-row">
                          <div className="world-cup-progress-row-label">
                            {inviteRegisterPercent >= 100 ?
                              <>
                                {t("WORLD_CUP_PARLAY_PROGRESS_REGISTERED_COMPLETED")}
                              </>
                              :
                              <>
                                {t("WORLD_CUP_PARLAY_PROGRESS_REGISTERED", {
                                  current: inviteRegisterCount,
                                  target: inviteCount,
                                })}
                              </>
                            }
                          </div>
                          <div className="world-cup-progress-bar">
                            <div
                              className="world-cup-progress-fill world-cup-progress-fill--register"
                              style={{ width: `${inviteRegisterPercent}%` }}
                            />
                          </div>
                        </div>
                        <div className="world-cup-progress-row">
                          <div className="world-cup-progress-row-label">
                            {inviteDepositPercent >= 100 ?
                              <>
                                {t("WORLD_CUP_PARLAY_PROGRESS_REGISTERED_COMPLETED")}
                              </>
                              :
                              <>
                                {t("WORLD_CUP_PARLAY_PROGRESS_DEPOSITED", {
                                  current: inviteDepositCount,
                                  target: inviteCount,
                                })}
                              </>
                            }
                            
                          </div>
                          <div className="world-cup-progress-bar">
                            <div
                              className="world-cup-progress-fill world-cup-progress-fill--register"
                              style={{ width: `${inviteDepositPercent}%` }}
                            />
                          </div>
                        </div>
                        <div className="world-cup-progress-row">
                          <div className="world-cup-progress-row-label">
                            {inviteTotalDepositPercent >= 100 ?
                              <>
                                {t("WORLD_CUP_PARLAY_PROGRESS_REGISTERED_COMPLETED")}
                              </>
                              :
                              <>
                                {t("WORLD_CUP_PARLAY_PROGRESS_DEPOSITED_AMOUNT", {
                                  current: totalDeposit,
                                  target: WINNER_DEPOSIT_TARGET,
                                })}
                              </>
                            }
                            
                          </div>
                          <div className="world-cup-progress-bar">
                            <div
                              className="world-cup-progress-fill world-cup-progress-fill--register"
                              style={{ width: `${inviteTotalDepositPercent}%` }}
                            />
                          </div>
                        </div>
                      </div>
                      <div className="world-cup-invite-desc">
                        {t("WORLD_CUP_PARLAY_INVITE_DESC", { count: inviteCount })}
                      </div>
                      <br />
                      <div className="world-cup-invite-desc">
                        {t("WORLD_CUP_PARLAY_CLAIM_DESC", { amount: inviteRewardAmount })}
                      </div>
                      {!isClaim ? (
                        <button
                          className="world-cup-btn world-cup-btn-ghost"
                          disabled={
                            isClaim ||
                            !isLocked ||
                            !(inviteRegisterCount >= inviteCount) ||
                            !(inviteDepositCount >= inviteCount) ||
                            !(totalDeposit >= WINNER_DEPOSIT_TARGET)
                          }
                          onClick={() => SubmitRewardClaim()}
                        >
                          {t("WORLD_CUP_PARLAY_CLAIM_REWARDS")}
                        </button>
                      ) : (
                        <button className="world-cup-btn world-cup-btn-ghost" type="button" disabled>
                          {t("CLAIMED")}
                        </button>
                      )}
                    </div>
                  ) : null}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {isWinnerClaimPopupOpen && (
        <div
          className="world-cup-confirm-overlay world-cup-winner-claim-overlay"
          onClick={() => setIsWinnerClaimPopupOpen(false)}
          role="presentation"
        >
          <div
            className="world-cup-confirm-card world-cup-winner-claim-card"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-labelledby="world-cup-winner-claim-title"
          >
            <button
              type="button"
              className="world-cup-winner-claim-close"
              onClick={() => setIsWinnerClaimPopupOpen(false)}
              aria-label={t("CLOSE")}
            >
              ×
            </button>
            <div id="world-cup-winner-claim-title" className="world-cup-winner-claim-title">
              <ParlaySectionTitle>{t("WINNER")}</ParlaySectionTitle>
            </div>
            {championTeam ? (
              <div className="world-cup-winner-claim-champion">
                {t("WORLD_CUP_PARLAY_MY_CHAMPION")}{" "}
                <span className="world-cup-winner-claim-champion-name">{championTeam.countryName}</span>
              </div>
            ) : null}
            <div className="world-cup-winner-claim-teams-grid">
              {selectedTeams.map((teamId) => {
                const selectedTeam = teamList.find((item) => item.id === teamId);
                const isChampion = championTeamId != null && teamId === championTeamId;
                return (
                  <div key={teamId} className="world-cup-winner-claim-team-chip">
                    {selectedTeam ? (
                      <>
                        <img
                          src={`/img/world-cup/${selectedTeam.code}.svg`}
                          alt=""
                          className="world-cup-winner-claim-team-flag"
                        />
                        <span className="world-cup-winner-claim-team-name">{selectedTeam.countryName}</span>
                        {isChampion ? (
                          <span className="world-cup-winner-claim-team-crown" aria-hidden="true">
                            <img src="/img/world-cup/crown.svg" alt={t("WORLD_CUP_PARLAY_ALT_CROWN")} />
                          </span>
                        ) : null}
                      </>
                    ) : (
                      <span className="world-cup-winner-claim-team-name">{teamId}</span>
                    )}
                  </div>
                );
              })}
            </div>
            <div>
              <div className="world-cup-modal-card-lottie-wrap" aria-hidden="true">
                {winnerResult.isWinner &&
                  <iframe className="congratulation-ifram-popup" src="https://lottie.host/embed/f53703f6-2f21-4c12-aba1-849edc808aca/qTbuVDWkTJ.json" />
                  }
              </div>
              <div className="world-cup-winner-claim-congrats-wrap">
                <img
                  src={winnerResult.isWinner ? "/img/world-cup/champoin.png" : ""}
                  alt={winnerResult.isWinner ? t("WORLD_CUP_PARLAY_ALT_CONGRATULATIONS") : t("")}
                  style={{ width: "50px" }}
                />
                <img src="/img/world-cup/Congratulations.png" alt={t("WORLD_CUP_PARLAY_ALT_CONGRATULATIONS")}
                  style={{ width: "200px" }} />
              </div>
            </div>
            <div className="world-cup-winner-claim-prize-copy">
              {t("YOU_HAVE")}<span>{t("WON_RM")} {winnerResult.winAmount}</span>
            </div>
            <div className="world-cup-winner-claim-progress-label">
              {winnerDepositPercent >= 100 ?
                <>{t("WORLD_CUP_PARLAY_WINNER_DEPOSIT_PROGRESS_COMPLETED")}</>
                :
                <>
                  {t("WORLD_CUP_PARLAY_WINNER_DEPOSIT_PROGRESS", {
                    current: winnerResult.totalDesposit,
                    target: WINNER_DEPOSIT_TARGET,
                  })}
                </>
              }
            </div>
            <div className="world-cup-progress-bar world-cup-winner-claim-progress-bar">
              <div
                className="world-cup-progress-fill world-cup-progress-fill--register"
                style={{ width: `${winnerDepositPercent}%` }}
              />
            </div>
            <div className="world-cup-winner-claim-actions">
              <button
                type="button"
                className="world-cup-btn world-cup-winner-claim-btn"
                disabled={!winnerResult.claimAvailable || winnerResult.isClaim}
                onClick={() => SubmitWinnerPrizeClaim()}
              >
                {winnerResult.isClaim ? t("CLAIMED") : "Claim Now"}
              </button>
            </div>
          </div>
        </div>
      )}

      {isWinnerClaimSuccessOpen && (
        <div
          className="world-cup-confirm-overlay world-cup-winner-claim-overlay"
          onClick={() => setIsWinnerClaimSuccessOpen(false)}
          role="presentation"
        >
          <div
            className="world-cup-confirm-card world-cup-winner-claim-card"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-labelledby="world-cup-winner-claim-success-title"
          >
            <div id="world-cup-winner-claim-success-title" className="world-cup-confirm-title">
              {t("CLAIM_SUCCESS")}
            </div>
            <div className="world-cup-confirm-actions world-cup-confirm-actions--single">
              <button
                type="button"
                className="world-cup-btn world-cup-btn-primary"
                onClick={() => setIsWinnerClaimSuccessOpen(false)}
              >
                {t("OK")}
              </button>
            </div>
          </div>
        </div>
      )}

      {isShareOpen && (
        <div className="world-cup-modal-overlay world-cup-modal-overlay--share">
          <div className="world-cup-share-card">

            <div className="world-cup-share-card-inner">
              {sharePanel === "pick" ? (
                <>
                  <button
                    type="button"
                    className="world-cup-close-btn"
                    onClick={() => {
                      setIsShareOpen(false);
                      setSharePanel("pick");
                    }}
                    aria-label={t("CLOSE")}
                  >
                    ×
                  </button>
                  <div className="world-cup-share-heading">
                    <span className="world-cup-parlay-section-line" aria-hidden="true" />
                    <span className="world-cup-parlay-section-diamond" aria-hidden="true" />
                    <span className="world-cup-share-heading-label">{t("WORLD_CUP_PARLAY_SHARE_YOUR_PICK")}</span>
                    <span className="world-cup-parlay-section-diamond" aria-hidden="true" />
                    <span className="world-cup-parlay-section-line" aria-hidden="true" />
                  </div>
                  {championTeam ? (
                    <div className="world-cup-share-champion-block">
                      <div className="world-cup-share-champion-label">{t("WORLD_CUP_PARLAY_MY_CHAMPION")}</div>
                      <div className="world-cup-share-champion-name">{championTeam.countryName}</div>
                    </div>
                  ) : null}

                  <div className="world-cup-share-teams">
                    {selectedTeams.map((teamId) => {
                      const selectedTeam = teamList.find((item) => item.id === teamId);
                      const isChampion = championTeamId != null && teamId === championTeamId;
                      return (
                        <div key={teamId} className="world-cup-share-team-card">
                          {selectedTeam ? (
                            <>
                              <img
                                src={`/img/world-cup/${selectedTeam.code}.svg`}
                                alt=""
                                className="world-cup-share-team-flag"
                              />
                              <span className="world-cup-share-team-name">{selectedTeam.countryName}</span>
                              {isChampion ? (
                                <span className="world-cup-share-team-crown" title={t("WORLD_CUP_PARLAY_CHAMPION")} aria-hidden="true">
                                  <img src="/img/world-cup/crown.svg" alt={t("WORLD_CUP_PARLAY_ALT_CROWN")} style={{ width: '25px' }} />
                                </span>
                              ) : null}
                            </>
                          ) : (
                            <span className="world-cup-share-team-name">{teamId}</span>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  <div className="world-cup-share-referral-row">
                    <div className="world-cup-share-code-box">
                      <div className="world-cup-share-code-row">
                        <span>{t("WORLD_CUP_PARLAY_REFERRAL_CODE")}</span>
                        <button
                          type="button"
                          className="world-cup-share-copy-btn"
                          onClick={copyReferralCode}
                          aria-label={t("WORLD_CUP_PARLAY_COPY_REFERRAL_ARIA")}
                        >
                          <img src="/img/world-cup/copy.svg" alt={t("WORLD_CUP_PARLAY_ALT_COPY")} style={{ width: '15px' }} />
                        </button>
                      </div>
                      <div className="world-cup-share-code-value">{referralCode}</div>
                    </div>
                    <div className="world-cup-share-qr">
                      {/*<span className="world-cup-share-qr-placeholder" aria-hidden="true" />*/}
                      {/*<span className="world-cup-share-qr-inner">QR</span>*/}
                      <QRCode
                        className="world-cup-referral-qr-code-img"
                        value={registerUrl}
                      />
                    </div>
                  </div>
                  <div className="world-cup-share-code-box register-url-button" onClick={copyRegisterUrl}>
                    <div className="register-url-div">{registerUrl}</div>
                    <div>
                      <img src="/img/world-cup/copy.svg" alt={t("WORLD_CUP_PARLAY_ALT_COPY")} style={{ width: "15px" }} />
                    </div>
                  </div>
                  <div className="world-cup-share-pool-text">
                    {t("WORLD_CUP_PARLAY_SHARE_POOL_BEFORE")}
                    <span className="world-cup-share-pool-highlight">{t("WORLD_CUP_PARLAY_SHARE_POOL_HIGHLIGHT")}</span>
                    {t("WORLD_CUP_PARLAY_SHARE_POOL_AFTER")}
                  </div>
                  <button
                    type="button"
                    className="world-cup-btn world-cup-share-now"
                    onClick={() => setSharePanel("channels")}
                  >
                    <img src="/img/world-cup/share.svg" alt={t("WORLD_CUP_PARLAY_ALT_SHARE")} style={{ width: '20px' }} />
                    {t("WORLD_CUP_PARLAY_SHARE_NOW")}
                  </button>
                </>
              ) : (
                <>
                  <div className="world-cup-share-heading">
                    <span className="world-cup-parlay-section-line" aria-hidden="true" />
                    <span className="world-cup-parlay-section-diamond" aria-hidden="true" />
                    <span className="world-cup-share-heading-label">{t("WORLD_CUP_PARLAY_SHARE_REFERRAL")}</span>
                    <span className="world-cup-parlay-section-diamond" aria-hidden="true" />
                    <span className="world-cup-parlay-section-line" aria-hidden="true" />
                  </div>
                  <div className="world-cup-share-referral-row world-cup-share-referral-row--channels">
                    <div className="world-cup-share-code-box">
                      <div className="world-cup-share-code-row">
                        <span>{t("WORLD_CUP_PARLAY_REFERRAL_CODE")}</span>
                        <button
                          type="button"
                          className="world-cup-share-copy-btn"
                          onClick={copyReferralCode}
                          aria-label={t("WORLD_CUP_PARLAY_COPY_REFERRAL_ARIA")}
                        >
                          <img src="/img/world-cup/copy.svg" alt={t("WORLD_CUP_PARLAY_ALT_COPY")} style={{ width: "15px" }} />
                        </button>
                      </div>
                      <div className="world-cup-share-code-value">{referralCode}</div>
                    </div>
                    <div className="world-cup-share-qr">
                      <QRCode className="world-cup-referral-qr-code-img" value={registerUrl} />
                    </div>
                  </div>
                  <div className="world-cup-share-via-title">{t("WORLD_CUP_PARLAY_SHARE_VIA")}</div>
                  {/*<div className="world-cup-share-social-grid">
                    <button type="button" className="world-cup-share-social-item" onClick={() => onClickShare() }>
                      <img src="/img/world-cup/Whatsapp.svg" alt="WhatsApp" className="world-cup-share-social-icon" />
                      <span>WhatsApp</span>
                    </button>
                        <button type="button" className="world-cup-share-social-item" onClick={() => onClickShare()}>
                      <img src="/img/world-cup/Facebook.svg" alt="Facebook" className="world-cup-share-social-icon" />
                      <span>Facebook</span>
                    </button>
                        <button type="button" className="world-cup-share-social-item" onClick={() => onClickShare()}>
                      <img src="/img/world-cup/X.svg" alt="X" className="world-cup-share-social-icon" />
                      <span>X</span>
                    </button>
                        <button type="button" className="world-cup-share-social-item" onClick={() => onClickShare()}>
                      <img src="/img/world-cup/WeChat.svg" alt="WeChat" className="world-cup-share-social-icon" />
                      <span>WeChat</span>
                    </button>
                        <button type="button" className="world-cup-share-social-item" onClick={() => onClickShare()}>
                      <img src="/img/world-cup/Telegram.svg" alt="Telegram" className="world-cup-share-social-icon" />
                      <span>Telegram</span>
                    </button>
                  </div>*/}
                  <div className="ref-share" style={{ padding: '20px 0px 0px' }}>
                    {ShareButton}
                  </div>
                  <button
                    type="button"
                    className="world-cup-btn world-cup-share-now world-cup-share-close-main-btn"
                    onClick={() => {
                      setIsShareOpen(false);
                      setSharePanel("pick");
                    }}
                  >
                    {t("CLOSE")}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {isMaxTeamsNoticeOpen && (
        <div className="world-cup-confirm-overlay">
          <div className="world-cup-confirm-card">
            <div className="world-cup-confirm-title">{t("WORLD_CUP_PARLAY_SELECTION_LIMIT")}</div>
            <div className="world-cup-confirm-content">
              {t("WORLD_CUP_PARLAY_MAX_TEAMS")}
            </div>
            <div className="world-cup-confirm-actions world-cup-confirm-actions--single">
              <button
                type="button"
                className="world-cup-btn world-cup-btn-primary"
                onClick={() => setIsMaxTeamsNoticeOpen(false)}
              >
                {t("WORLD_CUP_PARLAY_OK")}
              </button>
            </div>
          </div>
        </div>
      )}

      {submitErrorOpen && (
        <div className="world-cup-confirm-overlay">
          <div className="world-cup-confirm-card">
            <div className="world-cup-confirm-title">{t("ERROR")}</div>
            <div className="world-cup-confirm-content">{submitErrorMessage}</div>
            <div className="world-cup-confirm-actions world-cup-confirm-actions--single">
              <button
                type="button"
                className="world-cup-btn world-cup-btn-primary"
                onClick={() => {
                  setSubmitErrorOpen(false);
                  setSubmitErrorMessage("");
                }}
              >
                {t("OK")}
              </button>
            </div>
          </div>
        </div>
      )}

      {isLockConfirmOpen && referralCodeList.length > 0 ? (
        <div className="world-cup-confirm-overlay world-cup-confirm-overlay--lock">
          <div className="world-cup-confirm-card world-cup-confirm-card--lock">
            {lockConfirmStep === 1 ? (
              <>
                <div className="world-cup-confirm-heading">
                  <span className="world-cup-parlay-section-line" aria-hidden="true" />
                  <span className="world-cup-parlay-section-diamond" aria-hidden="true" />
                  <span className="world-cup-confirm-heading-label">{t("WORLD_CUP_PARLAY_TEAM_CONFIRMATION")}</span>
                  <span className="world-cup-parlay-section-diamond" aria-hidden="true" />
                  <span className="world-cup-parlay-section-line" aria-hidden="true" />
                </div>
                {championTeam ? (
                  <div className="world-cup-confirm-champion-block">
                    <div className="world-cup-share-champion-label">{t("WORLD_CUP_PARLAY_MY_CHAMPION")}</div>
                    <div className="world-cup-share-champion-name">{championTeam.countryName}</div>
                  </div>
                ) : null}
                <div className="world-cup-confirm-teams-grid">
                  {selectedTeams.map((teamId) => {
                    const selectedTeam = teamList.find((item) => item.id === teamId);
                    const isChampion = championTeamId != null && teamId === championTeamId;
                    return (
                      <div key={teamId} className="world-cup-share-team-card world-cup-confirm-team-card">
                        {selectedTeam ? (
                          <>
                            <img
                              src={`/img/world-cup/${selectedTeam.code}.svg`}
                              alt=""
                              className="world-cup-share-team-flag"
                            />
                            <span className="world-cup-share-team-name">{selectedTeam.countryName}</span>
                            {isChampion ? (
                              <span className="world-cup-share-team-crown" title={t("WORLD_CUP_PARLAY_CHAMPION")} aria-hidden="true">
                                <img src="/img/world-cup/crown.svg" alt={t("WORLD_CUP_PARLAY_ALT_CROWN")} style={{ width: '25px' }} />
                              </span>
                            ) : null}
                          </>
                        ) : (
                          <span className="world-cup-share-team-name">{teamId}</span>
                        )}
                      </div>
                    );
                  })}
                </div>
                <div className="world-cup-confirm-referral-row">
                  <select
                    className="world-cup-confirm-referral-select"
                    value={selectedReferralCodeId}
                    onChange={(e) => setSelectedReferralCodeId(e.target.value)}
                  >
                    {referralCodeList.length === 0 ? (
                      <option value="">{t("WORLD_CUP_PARLAY_SELECT_REFERRAL_GROUP")}</option>
                    ) : (
                      referralCodeList.map((item) => (
                        <option key={item.id} value={String(item.id)}>
                          {item.code}
                        </option>
                      ))
                    )}
                  </select>
                  <span className="world-cup-confirm-referral-chevron" aria-hidden="true">
                    ▼
                  </span>
                </div>
                <p className="world-cup-confirm-instruction">
                  {t("WORLD_CUP_PARLAY_LOCK_BEFORE")}
                  <span className="world-cup-confirm-gold-em">{t("WORLD_CUP_PARLAY_LOCK_EMPH")}</span>
                  {t("WORLD_CUP_PARLAY_LOCK_AFTER")}
                </p>
                    <div className="world-cup-confirm-actions world-cup-confirm-actions--lock">
                      <button type="button" className="world-cup-btn world-cup-confirm-btn-cancel" onClick={closeLockConfirm}>
                        {t("CANCEL")}
                      </button>
                      <button type="button" className="world-cup-btn world-cup-confirm-btn-lock" onClick={() => setLockConfirmStep(2)}>
                        {t("WORLD_CUP_PARLAY_LOCK")}
                      </button>
                    </div>
              </>
            ) : (
              <>
                <div className="world-cup-confirm-heading">
                  <span className="world-cup-parlay-section-line" aria-hidden="true" />
                  <span className="world-cup-parlay-section-diamond" aria-hidden="true" />
                  <span className="world-cup-confirm-heading-label">{t("WORLD_CUP_PARLAY_CONFIRM_LOCK_PICKS")}</span>
                  <span className="world-cup-parlay-section-diamond" aria-hidden="true" />
                  <span className="world-cup-parlay-section-line" aria-hidden="true" />
                </div>
                <div className="world-cup-confirm-warning-icon-wrap">
                  <img src="/img/world-cup/Warning.svg" alt={t("WORLD_CUP_PARLAY_ALT_WARNING")} />
                </div>
                <div className="world-cup-confirm-warning-copy">
                  <div className="world-cup-confirm-warning-title">{t("WORLD_CUP_PARLAY_WARNING_TITLE")}</div>
                  <p className="world-cup-confirm-warning-body">
                    {t("WORLD_CUP_PARLAY_WARNING_BEFORE")}
                    <span className="world-cup-confirm-gold-em">{t("WORLD_CUP_PARLAY_WARNING_EMPH")}</span>
                  </p>
                </div>
                <div className="world-cup-confirm-actions world-cup-confirm-actions--lock">
                  <button type="button" className="world-cup-btn world-cup-confirm-btn-cancel" onClick={closeLockConfirm}>
                    {t("CANCEL")}
                  </button>
                  <button type="button" className="world-cup-btn world-cup-confirm-btn-lock" onClick={() => SubmitLocked()}>
                    {t("CONFIRM")}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      ) : 
        <>
          {isLockConfirmOpen && referralCodeList.length == 0 &&
            <div className="world-cup-confirm-overlay world-cup-confirm-overlay--lock">
              <div className="world-cup-confirm-card world-cup-confirm-card--lock">
                <div className="warning-referral-code">
                  <img src="/img/world-cup/Warning.svg" alt={t("WORLD_CUP_PARLAY_ALT_WARNING")} />
                  {t("CREATE_TO_LOCK_WORLD_CUP_TEAM")}
                </div>
                <button type="button" className="world-cup-btn world-cup-confirm-btn-cancel" onClick={() => _history.push(WebUrl._URL_REFERRAL_CODE_DETAIL)}>
                  {t("CREATE_REFERRAL_CODE")}
                </button>
              </div>
            </div>
          }
        </>
        
      }

      {copySuccessVisible ? (
        <div className="world-cup-copy-success-toast" role="status" aria-live="polite">
          {t("COPY_SUCCESS")}
        </div>
      ) : null}
    </>
  );
};

export default WorldCupParlay;
